import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { upNextDueLabel, UpNextRow } from './up-next-row';

import { Text } from '@/components/ui';
import { useUpNext } from '@/hooks/use-up-next';
import { todayBusinessMs } from '@/utils/date-utils';

/**
 * "Up Next" — the maintenance replacement for the To Do panel: the work to start on, in the
 * order to start it.
 *
 * A SUMMARY, like To Do. `useUpNext` returns the fully ranked list; this shows the top few and
 * links to `/todo`, which renders the same list with the same `UpNextRow` for maintenance.
 *
 * Self-hides when there is nothing up next: an empty card trains people to skip it.
 */

/** How many rows before "…and N more". A dashboard card is a nudge, not the queue. */
const MAX_LISTED = 10;

export const UpNextPanel: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const { items } = useUpNext();

	if (items.length === 0) return null;

	const today = todayBusinessMs();
	const overdueCount = items.filter((item) => upNextDueLabel(item.dueMs, today)?.overdue).length;
	const listed = items.slice(0, MAX_LISTED);
	const remaining = items.length - listed.length;

	return (
		<View style={[styles.card, style]} testID="v2-up-next-panel">
			<View style={styles.header}>
				<Ionicons
					name={overdueCount > 0 ? 'alert-circle' : 'play-forward-outline'}
					size={18}
					color={overdueCount > 0 ? '#c62828' : '#546e7a'}
				/>
				<Text style={styles.title}>Up Next</Text>
				<View style={[styles.badge, overdueCount > 0 && styles.badgeOverdue]}>
					<Text style={styles.badgeText}>{items.length}</Text>
				</View>
				{overdueCount > 0 && (
					<Text numberOfLines={1} style={styles.overdueNote}>{overdueCount} overdue</Text>
				)}
				{/* A flexible spacer, not `marginLeft: 'auto'` — see the note on To Do's header. */}
				<View style={styles.spacer} />
				<Pressable
					onPress={() => router.push('/todo')}
					accessibilityRole="link"
					accessibilityLabel="View all up next"
					testID="v2-up-next-view-all"
				>
					<Text style={styles.link}>View all →</Text>
				</Pressable>
			</View>

			{listed.map((item) => (
				<UpNextRow key={item.key} item={item} />
			))}

			{remaining > 0 && (
				<Pressable
					onPress={() => router.push('/todo')}
					accessibilityRole="link"
					accessibilityLabel={`View ${remaining} more up next`}
					testID="v2-up-next-more"
				>
					<Text style={styles.more}>…and {remaining} more</Text>
				</Pressable>
			)}
		</View>
	);
};

// Same chrome as TodoPanel, so swapping one for the other leaves the layout untouched.
const styles = StyleSheet.create({
	card: {
		backgroundColor: '#fff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e8eaed',
		padding: 18,
	},
	// No `flexWrap`, deliberately — it would wrap "View all" to the left edge. See TodoPanel.
	header: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
	title: { fontSize: 16, fontWeight: '700', color: '#263238' },
	spacer: { flex: 1, minWidth: 8 },
	// minHeight, not height: a fixed 20pt box clips the count under iOS Dynamic Type.
	badge: {
		backgroundColor: '#546e7a', borderRadius: 10, minWidth: 22, minHeight: 20,
		alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6, paddingVertical: 1,
	},
	badgeOverdue: { backgroundColor: '#c62828' },
	badgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
	overdueNote: { fontSize: 12, fontWeight: '700', color: '#c62828', minWidth: 0, flexShrink: 1 },
	link: { color: '#1976d2', fontSize: 13, fontWeight: '500' },
	more: { color: '#1976d2', fontSize: 13, paddingTop: 10 },
});
